// AddManufacturingdata.js - Complete with Files, Notes & LotTracking Auto-Refresh
// ✅ Save buttons ABOVE the Files/Notes section
// ✅ Dispatches 'manufacturingDataCreated' event so LotTracking auto-refreshes
// ✅ UI: full-width cards, 3 fields per row
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FaSave, FaTimes, FaArrowLeft, FaIndustry, FaRuler } from 'react-icons/fa';
import Swal from 'sweetalert2';
import Details from './Files';
import Notes from './Notes';
import './AddManufacturingdata.css';

const AddManufacturingdata = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { shopId } = useParams();
  const editData = location.state?.editData;
  const isEditMode = !!editData;

  const detailsRef = useRef();

  // ==================== STATE ====================
  const [selectedShopId, setSelectedShopId] = useState('');
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shopName, setShopName] = useState('');

  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');

  const [manufacturingId, setManufacturingId] = useState(editData?.id || null);
  const [activeTab, setActiveTab] = useState('details');

  const isAllShopsMode = shopId === 'all' || !shopId;
  const isSpecificShopMode = shopId && shopId !== 'all';

  // Form fields
  const [manufacturingDate, setManufacturingDate] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [machineNumber, setMachineNumber] = useState('');
  const [materialLotNumber, setMaterialLotNumber] = useState('');
  const [internalLotNumber, setInternalLotNumber] = useState('');
  const [partName, setPartName] = useState('');
  const [partType, setPartType] = useState('');
  const [partReferenceNumber, setPartReferenceNumber] = useState('');
  const [machineOperator, setMachineOperator] = useState('');
  const [machineInspection, setMachineInspection] = useState('');
  const [partPerWeight, setPartPerWeight] = useState('');
  const [totalPartWeight, setTotalPartWeight] = useState('');
  const [quantity, setQuantity] = useState('');
  const [partLengthMm, setPartLengthMm] = useState('');
  const [partLengthIncludeWaste, setPartLengthIncludeWaste] = useState('');
  const [partManufacturingWastageLength, setPartManufacturingWastageLength] = useState('');
  const [partManufacturingWastageLengthNos, setPartManufacturingWastageLengthNos] = useState('');
  const [issueBarForManufacturing, setIssueBarForManufacturing] = useState('');
  const [returnBalanceBarNos, setReturnBalanceBarNos] = useState('');
  const [returnBalanceBarMm, setReturnBalanceBarMm] = useState('');
  const [perLengthMmWastage150mm, setPerLengthMmWastage150mm] = useState('');
  const [wastage150mm, setWastage150mm] = useState('');
  const [finalUsedBarNos, setFinalUsedBarNos] = useState('');
  const [finalUsedBarMm, setFinalUsedBarMm] = useState('');
  const [eachPartWaste, setEachPartWaste] = useState('');

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

  // ==================== SWEETALERT HELPERS ====================
  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      timer: 2000,
      showConfirmButton: true,
      confirmButtonColor: '#3085d6',
    });
  };

  const showErrorAlert = (message, details = '') => {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      html: `
        <div style="text-align: left;">
          <p style="margin: 0 0 8px 0; font-size: 14px;">${message}</p>
          ${details ? `<pre style="background: #f5f5f5; padding: 10px; border-radius: 6px; font-size: 12px; color: #d33; text-align: left; white-space: pre-wrap; max-height: 200px; overflow-y: auto; margin: 0;">${details}</pre>` : ''}
        </div>
      `,
      confirmButtonColor: '#d33',
      width: '500px',
    });
  };

  // ==================== FETCH CITIES ====================
  const fetchCities = async (shopIdParam) => {
    if (!shopIdParam) {
      setCities([]);
      setSelectedCity('');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/inventory/shop-cities/?shop_id=${shopIdParam}`);
      if (response.ok) {
        const data = await response.json();
        if (data.cities && data.cities.length > 0) {
          setCities(data.cities);
          if (!selectedCity && editData?.city) setSelectedCity(editData.city);
        } else {
          setCities([]);
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    }
  };

  // ==================== FETCH SHOPS ====================
  const fetchShops = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/inventory/shops/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setShops(Array.isArray(data) ? data : []);

        if (isSpecificShopMode && data.length > 0) {
          const currentShop = data.find((shop) => shop.shop_id.toString() === shopId);
          if (currentShop) {
            setShopName(currentShop.shop_name);
            setSelectedShopId(shopId);
            if (currentShop.cities && currentShop.cities.length > 0) {
              setCities(currentShop.cities);
            } else {
              await fetchCities(shopId);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  useEffect(() => {
    fetchShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  // ==================== LOAD EDIT DATA ====================
  useEffect(() => {
    if (isEditMode && editData) {
      setManufacturingId(editData.id);
      setManufacturingDate(editData.manufacturing_date || '');
      setLotNumber(editData.lot_number || '');
      setMachineNumber(editData.machine_number || '');
      setMaterialLotNumber(editData.material_lot_number || '');
      setInternalLotNumber(editData.internal_lot_number || '');
      setPartName(editData.part_name || '');
      setPartType(editData.part_type || '');
      setPartReferenceNumber(editData.part_reference_number || '');
      setMachineOperator(editData.machine_operator || '');
      setMachineInspection(editData.machine_inspection || '');
      setPartPerWeight(editData.part_per_weight || '');
      setTotalPartWeight(editData.total_part_weight || '');
      setQuantity(editData.quantity || '');
      setPartLengthMm(editData.part_length_mm || '');
      setPartLengthIncludeWaste(editData.part_length_mm_include_waste || '');
      setPartManufacturingWastageLength(editData.part_manufacturing_with_wastage_length_mm || '');
      setPartManufacturingWastageLengthNos(editData.part_manufacturing_with_wastage_length_nos || '');
      setIssueBarForManufacturing(editData.issue_bar_for_manufacturing || '');
      setReturnBalanceBarNos(editData.return_balance_bar_in_nos || '');
      setReturnBalanceBarMm(editData.return_balance_bar_in_mm || '');
      setPerLengthMmWastage150mm(editData.per_length_mm_wastage_150mm || '');
      setWastage150mm(editData.wastage_150mm || '');
      setFinalUsedBarNos(editData.final_used_bar_in_nos || '');
      setFinalUsedBarMm(editData.final_used_bar_in_mm || '');
      setEachPartWaste(editData.each_part_waste || '');

      if (editData.shop) {
        setSelectedShopId(editData.shop.shop_id.toString());
        if (editData.city) setSelectedCity(editData.city);
        fetchCities(editData.shop.shop_id.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editData]);

  // ==================== HANDLE SHOP CHANGE ====================
  const handleShopChange = async (e) => {
    const shopIdValue = e.target.value;
    setSelectedShopId(shopIdValue);
    setSelectedCity('');
    if (shopIdValue) {
      await fetchCities(shopIdValue);
      const shop = shops.find((s) => s.shop_id.toString() === shopIdValue);
      if (shop) setShopName(shop.shop_name);
    } else {
      setCities([]);
      setShopName('');
    }
  };

  // ==================== SUBMIT ====================
  const handleSubmit = async (e) => {
    e.preventDefault();

    let finalShopId = selectedShopId;

    if (isAllShopsMode && !finalShopId) {
      showErrorAlert('Please select a shop', 'Shop selection is required');
      return;
    }
    if (isSpecificShopMode && !finalShopId) finalShopId = shopId;
    if (!finalShopId) {
      showErrorAlert('Shop selection is required', 'Please select a shop before saving');
      return;
    }
    if (!manufacturingDate || !lotNumber || !partName) {
      showErrorAlert(
        'Please fill in all required fields',
        'Manufacturing Date, Lot Number, and Part Name are required'
      );
      return;
    }

    const formData = {
      shop_id: parseInt(finalShopId),
      city: selectedCity || null,
      manufacturing_date: manufacturingDate,
      lot_number: lotNumber,
      part_name: partName,
      part_type: partType || null,
      part_reference_number: partReferenceNumber || null,
      machine_operator: machineOperator || null,
      machine_inspection: machineInspection || null,
      part_per_weight: partPerWeight !== '' ? parseFloat(partPerWeight) : null,
      total_part_weight: totalPartWeight !== '' ? parseFloat(totalPartWeight) : null,
      quantity: quantity !== '' ? parseInt(quantity) : null,
      machine_number: machineNumber !== '' ? parseInt(machineNumber) : null,
      material_lot_number: materialLotNumber || null,
      internal_lot_number: internalLotNumber || null,
      part_length_mm: partLengthMm !== '' ? parseFloat(partLengthMm) : null,
      part_length_mm_include_waste: partLengthIncludeWaste !== '' ? parseFloat(partLengthIncludeWaste) : null,
      part_manufacturing_with_wastage_length_mm: partManufacturingWastageLength !== '' ? parseFloat(partManufacturingWastageLength) : null,
      part_manufacturing_with_wastage_length_nos: partManufacturingWastageLengthNos !== '' ? parseFloat(partManufacturingWastageLengthNos) : null,
      issue_bar_for_manufacturing: issueBarForManufacturing !== '' ? parseFloat(issueBarForManufacturing) : null,
      return_balance_bar_in_nos: returnBalanceBarNos !== '' ? parseFloat(returnBalanceBarNos) : null,
      return_balance_bar_in_mm: returnBalanceBarMm !== '' ? parseFloat(returnBalanceBarMm) : null,
      per_length_mm_wastage_150mm: perLengthMmWastage150mm !== '' ? parseFloat(perLengthMmWastage150mm) : null,
      wastage_150mm: wastage150mm !== '' ? parseFloat(wastage150mm) : null,
      final_used_bar_in_nos: finalUsedBarNos !== '' ? parseFloat(finalUsedBarNos) : null,
      final_used_bar_in_mm: finalUsedBarMm !== '' ? parseFloat(finalUsedBarMm) : null,
      each_part_waste: eachPartWaste !== '' ? parseFloat(eachPartWaste) : null,
    };

    console.log('📤 Submitting payload:', JSON.stringify(formData, null, 2));

    try {
      setLoading(true);

      let url = `${API_BASE_URL}/inventory/api/manufacturing/create/`;
      let method = 'POST';
      if (isEditMode) {
        url = `${API_BASE_URL}/inventory/api/manufacturing/${editData.id}/update/`;
        method = 'PUT';
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const responseText = await response.text();
      console.log('📥 Status:', response.status);
      console.log('📥 Body:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        data = { error: responseText };
      }

      if (!response.ok) {
        let errorMessage = 'Failed to save manufacturing data';
        let errorDetails = '';

        if (typeof data === 'object' && data !== null) {
          if (data.error) errorMessage = data.error;
          if (data.message) errorMessage = data.message;

          const fieldErrors = Object.entries(data)
            .filter(([k, v]) =>
              k !== 'error' &&
              k !== 'message' &&
              k !== 'detail' &&
              (Array.isArray(v) || typeof v === 'string')
            )
            .map(([field, errs]) => {
              const errList = Array.isArray(errs) ? errs : [errs];
              return `${field}:\n  ${errList.join('\n  ')}`;
            })
            .join('\n\n');

          if (fieldErrors) errorDetails = fieldErrors;
          if (data.detail) errorMessage = data.detail;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }

        console.error('❌ Backend error:', data);
        showErrorAlert(errorMessage, errorDetails);
        return;
      }

      const savedId = data.id || data.manufacturing_id || editData?.id;
      console.log('✅ Saved ID:', savedId);
      setManufacturingId(savedId);

      // Dispatch event so LotTracking auto-refreshes
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('manufacturingDataCreated', {
          detail: {
            id: savedId,
            shopId: finalShopId,
            city: selectedCity || null,
          },
        }));
        console.log('📢 Dispatched manufacturingDataCreated event:', {
          id: savedId,
          shopId: finalShopId,
          city: selectedCity,
        });
      }

      let filesUploaded = 0;
      if (savedId && detailsRef.current?.hasUnsavedFiles?.()) {
        console.log('📤 Auto-uploading pending files...');
        const fileResult = await detailsRef.current.saveFiles(savedId);
        if (fileResult?.success) filesUploaded = fileResult.uploaded || 0;
      }

      const msg = isEditMode
        ? 'Manufacturing data updated successfully!'
        : 'Manufacturing data saved successfully!';

      showSuccessAlert(
        filesUploaded > 0
          ? `${msg} (${filesUploaded} file(s) uploaded)`
          : msg
      );

      setTimeout(() => navigate('/Manufacturingdata'), 1200);
    } catch (err) {
      console.error('❌ Network error:', err);
      showErrorAlert('Failed to save manufacturing data', err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== RESET ====================
  const handleReset = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'All unsaved data will be lost!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, reset it!',
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedShopId('');
        setSelectedCity('');
        setManufacturingDate('');
        setLotNumber('');
        setMachineNumber('');
        setMaterialLotNumber('');
        setInternalLotNumber('');
        setPartName('');
        setPartType('');
        setPartReferenceNumber('');
        setMachineOperator('');
        setMachineInspection('');
        setPartPerWeight('');
        setTotalPartWeight('');
        setQuantity('');
        setPartLengthMm('');
        setPartLengthIncludeWaste('');
        setPartManufacturingWastageLength('');
        setPartManufacturingWastageLengthNos('');
        setIssueBarForManufacturing('');
        setReturnBalanceBarNos('');
        setReturnBalanceBarMm('');
        setPerLengthMmWastage150mm('');
        setWastage150mm('');
        setFinalUsedBarNos('');
        setFinalUsedBarMm('');
        setEachPartWaste('');
        showSuccessAlert('Form reset successfully!');
      }
    });
  };

  // ==================== CANCEL ====================
  const handleCancel = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'All unsaved data will be lost!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, cancel it!',
    }).then((result) => {
      if (result.isConfirmed) navigate('/Manufacturingdata');
    });
  };

  // ==================== RENDER ====================
  return (
    <div className="add-manufacturing-container">
      <div className="form-header-section">
        <h2>{isEditMode ? 'Edit Manufacturing Data' : 'Add Manufacturing Data'}</h2>
        <button type="button" className="back-button" onClick={handleCancel}>
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="manufacturing-form-container">
        {/* ==================== BASIC INFO ==================== */}
        <div className="form-section">
          <h3 className="section-title">
            Basic Information
          </h3>

          <div className="form-grid">
            <div className="form-group">
              <label>
                Shop <span className="required">*</span>
              </label>
              {isAllShopsMode && !isEditMode ? (
                <select
                  value={selectedShopId}
                  onChange={handleShopChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select Shop</option>
                  {shops.map((shop) => (
                    <option key={shop.shop_id} value={shop.shop_id}>
                      {shop.shop_display || shop.shop_name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="shop-info-display">
                  <span>
                    {isEditMode && editData?.shop
                      ? `${editData.shop.shop_name} (ID: ${editData.shop.shop_id})`
                      : isSpecificShopMode
                      ? `${shopName} (ID: ${shopId})`
                      : 'No shop selected'}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>
                Manufacturing Date <span className="required">*</span>
              </label>
              <input
                type="date"
                value={manufacturingDate}
                onChange={(e) => setManufacturingDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>City</label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                disabled={loading || cities.length === 0 || !selectedShopId}
              >
                <option value="">Select City</option>
                {cities.map((city, i) => (
                  <option key={i} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                Lot Number <span className="required">*</span>
              </label>
              <input
                type="text"
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                placeholder="Enter lot number"
                required
              />
            </div>

            <div className="form-group">
              <label>Machine Number</label>
              <input
                type="number"
                value={machineNumber}
                onChange={(e) => setMachineNumber(e.target.value)}
                placeholder="Enter machine number"
              />
            </div>

            <div className="form-group">
              <label>Material Lot Number</label>
              <input
                type="text"
                value={materialLotNumber}
                onChange={(e) => setMaterialLotNumber(e.target.value)}
                placeholder="Enter material lot number"
              />
            </div>

            <div className="form-group">
              <label>Internal Lot Number</label>
              <input
                type="text"
                value={internalLotNumber}
                onChange={(e) => setInternalLotNumber(e.target.value)}
                placeholder="Enter internal lot number"
              />
            </div>

            <div className="form-group">
              <label>
                Part Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={partName}
                onChange={(e) => setPartName(e.target.value)}
                placeholder="Enter part name"
                required
              />
            </div>

            <div className="form-group">
              <label>Part Type</label>
              <input
                type="text"
                value={partType}
                onChange={(e) => setPartType(e.target.value)}
                placeholder="Enter part type"
              />
            </div>

            <div className="form-group">
              <label>Part Reference Number</label>
              <input
                type="text"
                value={partReferenceNumber}
                onChange={(e) => setPartReferenceNumber(e.target.value)}
                placeholder="Enter reference number"
              />
            </div>

            <div className="form-group">
              <label>Machine Operator</label>
              <input
                type="text"
                value={machineOperator}
                onChange={(e) => setMachineOperator(e.target.value)}
                placeholder="Enter operator name"
              />
            </div>

            <div className="form-group">
              <label>Machine Inspection</label>
              <select
                value={machineInspection}
                onChange={(e) => setMachineInspection(e.target.value)}
              >
                <option value="">Select status</option>
                <option value="Passed">Passed</option>
                <option value="Failed">Failed</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            <div className="form-group">
              <label>Part Per Weight (kg)</label>
              <input
                type="number"
                step="0.01"
                value={partPerWeight}
                onChange={(e) => setPartPerWeight(e.target.value)}
                placeholder="Weight per part"
              />
            </div>

            <div className="form-group">
              <label>Total Part Weight (kg)</label>
              <input
                type="number"
                step="0.01"
                value={totalPartWeight}
                onChange={(e) => setTotalPartWeight(e.target.value)}
                placeholder="Total weight"
              />
            </div>

            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Enter quantity"
              />
            </div>
          </div>
        </div>

        {/* ==================== WASTAGE ==================== */}
        <div className="form-section">
          <h3 className="section-title">
         Part Length &amp; Wastage Information
          </h3>

          <div className="form-grid">
            <div className="form-group">
              <label>Part Length (mm)</label>
              <input
                type="number"
                step="0.01"
                value={partLengthMm}
                onChange={(e) => setPartLengthMm(e.target.value)}
                placeholder="Part length in mm"
              />
            </div>

            <div className="form-group">
              <label>Part Length mm Include Waste per part 1.4mm</label>
              <input
                type="number"
                step="0.01"
                value={partLengthIncludeWaste}
                onChange={(e) => setPartLengthIncludeWaste(e.target.value)}
                placeholder="Length including waste"
              />
            </div>

            <div className="form-group">
              <label>Part Manufacturing With Wastage Length (mm)</label>
              <input
                type="number"
                step="0.01"
                value={partManufacturingWastageLength}
                onChange={(e) => setPartManufacturingWastageLength(e.target.value)}
                placeholder="Manufacturing length with wastage"
              />
            </div>

            <div className="form-group">
              <label>Part Manufacturing With Wastage Length (No's)</label>
              <input
                type="number"
                value={partManufacturingWastageLengthNos}
                onChange={(e) => setPartManufacturingWastageLengthNos(e.target.value)}
                placeholder="Number of pieces with wastage"
              />
            </div>

            <div className="form-group">
              <label>Issue Bar for Manufacturing</label>
              <input
                type="number"
                step="0.01"
                value={issueBarForManufacturing}
                onChange={(e) => setIssueBarForManufacturing(e.target.value)}
                placeholder="Issue bar details"
              />
            </div>

            <div className="form-group">
              <label>Return Balance Bar (No's)</label>
              <input
                type="number"
                step="0.01"
                value={returnBalanceBarNos}
                onChange={(e) => setReturnBalanceBarNos(e.target.value)}
                placeholder="Return balance bar in numbers"
              />
            </div>

            <div className="form-group">
              <label>Return Balance Bar (mm)</label>
              <input
                type="number"
                step="0.01"
                value={returnBalanceBarMm}
                onChange={(e) => setReturnBalanceBarMm(e.target.value)}
                placeholder="Return balance bar in mm"
              />
            </div>

            <div className="form-group">
              <label>
                Per Length mm Wastage 150mm <span className="required">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={perLengthMmWastage150mm}
                onChange={(e) => setPerLengthMmWastage150mm(e.target.value)}
                placeholder="Per length mm wastage"
                required
              />
            </div>

            <div className="form-group">
              <label>Wastage 150mm</label>
              <input
                type="number"
                step="0.01"
                value={wastage150mm}
                onChange={(e) => setWastage150mm(e.target.value)}
                placeholder="Wastage for 150mm"
              />
            </div>

            <div className="form-group">
              <label>Final Used Bar (No's)</label>
              <input
                type="number"
                step="0.01"
                value={finalUsedBarNos}
                onChange={(e) => setFinalUsedBarNos(e.target.value)}
                placeholder="Final used bar in numbers"
              />
            </div>

            <div className="form-group">
              <label>Final Used Bar (mm)</label>
              <input
                type="number"
                step="0.01"
                value={finalUsedBarMm}
                onChange={(e) => setFinalUsedBarMm(e.target.value)}
                placeholder="Final used bar in mm"
              />
            </div>

            <div className="form-group">
              <label>Each Part Waste</label>
              <input
                type="number"
                step="0.01"
                value={eachPartWaste}
                onChange={(e) => setEachPartWaste(e.target.value)}
                placeholder="Waste per part"
              />
            </div>
          </div>
        </div>

        {/* ==================== ACTION BUTTONS (above Files/Notes) ==================== */}
        <div className="form-actions form-actions-top">
          <button type="button" className="btn-cancel" onClick={handleCancel} disabled={loading}>
           Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            <FaSave /> {isEditMode ? 'Update Manufacturing Data' : 'Save Manufacturing Data'}
          </button>
        </div>

        {/* ==================== FILES & NOTES TABS ==================== */}
        <div className="details-notes-section">
          <div className="details-notes-tabs">
            <button
              type="button"
              className={`details-notes-tab ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details (Files)
            </button>
            <button
              type="button"
              className={`details-notes-tab ${activeTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              Notes
            </button>
          </div>

          <div className="details-notes-content">
            <div style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
              <Details
                ref={detailsRef}
                trackingId={manufacturingId || 'new'}
                mode="manufacturing"
                isNew={!manufacturingId}
                isEdit={isEditMode}
              />
            </div>

            <div style={{ display: activeTab === 'notes' ? 'block' : 'none' }}>
              <Notes trackingId={manufacturingId || 'new'} mode="manufacturing" />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddManufacturingdata;